/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
$(document).ready(function() {
  let total_price = 0;
  $('.custom-list-box > .card').each(function() {
    if($(this).find('.btn-add').hasClass('active')) {
      let item_price = $(this).find('.card__price').attr('data-price');
      item_price = item_price * parseInt($(this).find('.custom-unit-box span').html());
      total_price = total_price + item_price;
      let newContent = '<div class="item-box" data-id="p-' + $(this).find("input[name='id']").attr("value") + '">' +
        '<div class="item-image">' +
          '<img src="' + $(this).find('.card__wrapper img').attr('src').replace('{width}','300' ) + '">' +
        '</div>' + 
        '<div class="item-description">' +
          '<div class="item-title">' + $(this).find('.card__name').html() + '</div>' +
          '<div class="item-price-qty">' +
            '<div class="item-price">' + $(this).find('.card__price').attr('data-dis-price') + '</div>' +
            '<div class="item-qty"><span>x' + $(this).find('.custom-unit-box span').html() + '</span>' + $(this).find('.card__price').attr('data-unit') + '</div>' +
          '</div>' +
        '</div>' +
      '</div>';
      document.querySelector(".custom-footer-left").innerHTML = document.querySelector(".custom-footer-left").innerHTML + newContent;
      if(!$('.custom-price-list').hasClass('active'))
        $('.custom-price-list').addClass('active');
    }
  });
  total_price = total_price.toFixed(2);
  $('.custom-footer-right').attr('data-total',total_price);
  total_price = total_price.toString().replace(".", ",");  
  $('.custom-footer-right p span').html(total_price);
  
  $('.btn-add').on('click', function() {
    if($('.site-header__cart-bubble').html() == '') {
      $('.site-header__cart-bubble').addClass('site-header__cart-bubble--visible').html(1);
      $('.cart-total-price').addClass('site-header__cart-price--visible');
      $('.site-header__cart').addClass('active');
    } else {
      $('.site-header__cart-bubble').html(parseInt($('.site-header__cart-bubble').html()) + 1);
    }
    $(this).addClass('active');
    $(this).next().find('.custom-unit-box span').html(1);
    $(this).next().addClass('active');

    let avail_quant = $(this).closest('.col-form').find('#quantityInput').attr("quantity");
    
    if(parseInt(avail_quant) == 1){
      $(this).closest('.col-form').addClass('customvari');
    }
    
    var data = {
      "id": $(this).next().find("input[name='id']").attr("value"),
      "quantity": 1
    }
    $.ajax({
      url: "/cart/add.js",
      type: "post",
      data: data,        
      dataType: "application/json",
      success: function(){
        document.dispatchEvent(
          new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
        );
      },
      error: function(){
        document.dispatchEvent(
          new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
        );
      }
    });
    let added_total = parseFloat($('.custom-footer-right').attr('data-total'));
    added_total = added_total + parseFloat($(this).parent().parent().find('.card__price').attr('data-price'));
    added_total = added_total.toFixed(2);
    $('.custom-footer-right').attr('data-total',added_total);
    added_total = added_total.toString().replace(".", ",");  
    $('.custom-footer-right p span').html(added_total);
    $('.cart-total-price span:last-child').html('€ ' + added_total);
    let newContent = '<div class="item-box" data-id="p-' + $(this).next().find("input[name='id']").attr("value") + '">' +
      '<div class="item-image">' +
        '<img src="' + $(this).parent().parent().parent().find('.card__wrapper img').attr('src').replace('{width}','300' ) + '">' +
      '</div>' + 
      '<div class="item-description">' +
        '<div class="item-title">' + $(this).parent().parent().find('.card__name').html() + '</div>' +
        '<div class="item-price-qty">' +
          '<div class="item-price">' + $(this).parent().parent().find('.card__price').attr('data-dis-price') + '</div>' +
          '<div class="item-qty"><span>x1</span>' + $(this).parent().parent().find('.card__price').attr('data-unit') + '</div>' +
        '</div>' +
      '</div>' +
    '</div>';
    document.querySelector(".custom-footer-left").innerHTML = document.querySelector(".custom-footer-left").innerHTML + newContent;
    if(!$('.custom-price-list').hasClass('active'))
      $('.custom-price-list').addClass('active');
  });
  
  $('.btn-minus').on('click', function() {
    let qta = parseInt($(this).parent().prev().find('span').html());
    qta = qta - 1;    
    if(qta > 0) {      
      $(this).parent().prev().find('span').html(qta);
      $('.custom-footer-left .item-box[data-id="p-' + $(this).parent().find("input[name='id']").attr("value") + '"]').find('.item-qty span').html('x' + $(this).parent().prev().find('span').html());    
    }
    else {      
      $(this).parent().parent().removeClass('active');
      $(this).parent().parent().prev().removeClass('active');
      //$('.custom-price-list.active').removeClass('active');
      $('.custom-footer-left .item-box[data-id="p-' + $(this).parent().find("input[name='id']").attr("value") + '"]').remove();
    }
    let cart = parseInt($('.site-header__cart-bubble').html());
    if(cart > 1)
      $('.site-header__cart-bubble').html(cart - 1);
    else {
      $('.site-header__cart-bubble').html('');
      $('.site-header__cart-bubble').removeClass('site-header__cart-bubble--visible');
      $('.site-header__cart .cart-total-price').removeClass('site-header__cart-price--visible');
      $('.site-header__cart').removeClass('active');      
    }      
    var data = {
      "id": $(this).parent().find("input[name='id']").attr("value"),
      "quantity": qta
    }
    let added_total = parseFloat($('.custom-footer-right').attr('data-total'));
    added_total = added_total - parseFloat($(this).closest('.card').find('.card__price').attr('data-price'));
    added_total = added_total.toFixed(2);
    $('.custom-footer-right').attr('data-total',added_total);
    added_total = added_total.toString().replace(".", ",");  
    $('.custom-footer-right p span').html(added_total);
    if( added_total == '0,00'){
      $('.custom-price-list.active').removeClass('active');
    }
    $('.cart-total-price span:last-child').html('€ ' + added_total);
    $.ajax({
      url: "/cart/change.js",
      type: "post",
      data: data,
      dataType: "application/json",
      success: function(){
        document.dispatchEvent(
          new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
        );
      },
      error: function(){
        document.dispatchEvent(
          new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
        );
      }
    });

  });
  
  $('.btn-plus').on('click', function() {
    let qta = parseInt($(this).parent().prev().find('span').html());
    qta = qta + 1;
    $(this).parent().prev().find('span').html(qta);
    let cart = parseInt($('.site-header__cart-bubble').html());
    $('.site-header__cart-bubble').html(cart + 1);
    var data = {
      "id": $(this).parent().find("input[name='id']").attr("value"),
      "quantity": qta
    }
    let added_total = parseFloat($('.custom-footer-right').attr('data-total'));
    added_total = added_total + parseFloat($(this).closest('.card').find('.card__price').attr('data-price'));
    added_total = added_total.toFixed(2);
    $('.custom-footer-right').attr('data-total',added_total);
    added_total = added_total.toString().replace(".", ",");  
    $('.custom-footer-right p span').html(added_total);
    $('.cart-total-price span:last-child').html('€ ' + added_total);
    $.ajax({
      url: "/cart/change.js",
      type: "post",
      data: data,
      dataType: "application/json",
      success: function(e){
        console.log(e)
        document.dispatchEvent(
          new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
        );
      },
      error: function(e){
        document.dispatchEvent(
          new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
        );
      }
    });
    $('.custom-footer-left .item-box[data-id="p-' + $(this).parent().find("input[name='id']").attr("value") + '"]').find('.item-qty span').html('x' + $(this).parent().prev().find('span').html());    
  });
  $('.cart-item__qty input').on('change', function() {
    location.href = '/cart/change?line=' + $(this).closest('.cart-item').attr('data-cart-item-line-number') + '&quantity=' + $(this).val();    
  }); 
  /*
  $('.site-header__cart').on('click', function(e) {
    if(location.href.indexOf('collections') >= 0) {
      e.preventDefault();
      e.stopPropagation();
      location.href = '/cart';
    }
  });
  */
  $('body').on('click','.product-popup-wrapper .product-thumbnail-item', function() {
    let newImg = $(this).html();
    $('.product__media-container').html(newImg);
  });    
  $('body').on('click','.product__atc__btn', function() {
    let obj = $('#selected_product_id').val();
    if($('#' + obj).find('.btn-custom-wrapper').hasClass('active'))
      $('#' + obj).find('.btn-plus').trigger('click');
    else 
      $('#' + obj).find('.btn-add').trigger('click');
    $('.product-popup-wrapper .fancybox-button.fancybox-close-small').trigger('click');
  });
  $( ".navigation__has-sublinks" ).hover(
    function(){
      $(this).removeClass('navigation__has-sublinks--collapsed');
    },
    function(){
      $(this).addClass('navigation__has-sublinks--collapsed');
    }
  );
  $('.nav___search .navigation__search input').on('click', function(e) {
    e.preventDefault();
    e.stopPropagation();
  });
  $('.nav___search').on('click', function() {
    if($(this).find('.navigation__search').hasClass('active'))
      $(this).find('.navigation__search').removeClass('active');
    else
      $(this).find('.navigation__search').addClass('active');
  });  
  $('.card__wrapper').on('click', function(e) {
    console.log('card wrapper click');
    $('#selected_product_id').val($(this).parent().attr('id'));
    let product = JSON.parse($('#product-infos').text()).offers;
    let selected = $(this).next().find('.card__name').html();
    $.each(product, function(i, v) {
      var item = $('.product-popup-wrapper');
      if(v.name == selected) {
        let s_name = v.name.split(" ");
        let other_name = ' ';
        for(i=1; i< s_name.length; i++)
          other_name = other_name + s_name[i] + ' ';
        item.find('.product__title').html(s_name[0] + ' <span>' + other_name + '</span>');
        item.find('.product__title').css('color', v.pcolor);
        item.find('.product__current-price').html(v.price);
        item.find('.product__unit').html(v.unit);
        item.find('.product__description_box').html(v.description);
        item.find('.product-above-top-atc img').attr('src', v.pimg);
        item.find('.product-above-top-atc > div:first-child span').html(v.puvalue);
        if(v.pudes == null)
          item.find('.product-above-top-atc > div:last-child').css('display','none');
        else {
          item.find('.product-above-top-atc > div:last-child').css('display','block');
          item.find('.product-above-top-atc > div:last-child span').html(v.pudes);
        }
        item.find('.product-thumbnail-wrapper').html('');
        let nav1, nav2;
        $.each(v.image, function(j,p) {
          if(j == 0) {
            item.find('.product__media-container').html('<img src="' + p.src + '">');
            item.find('.product-thumbnail-wrapper').html(item.find('.product-thumbnail-wrapper').html() + '<div class="product-thumbnail-item"><img src="' + p.src + '" class="product-media-item"></div>');
            nav1 = '<div class="product-thumbnail-item"><img src="' + p.src + '" class="product-media-item"></div>';
          } else if(j ==1) {
            item.find('.product-thumbnail-wrapper').html(item.find('.product-thumbnail-wrapper').html() + '<div class="product-thumbnail-item"><img src="' + p.src + '" class="product-media-item"></div>');
            nav2 = '<div class="product-thumbnail-item"><img src="' + p.src + '" class="product-media-item"></div>';
          } else {
            item.find('.product-thumbnail-wrapper').html(item.find('.product-thumbnail-wrapper').html() + '<div class="product-thumbnail-item"><img src="' + p.src + '" class="product-media-item"></div>');
          }
        });
        item.find('.product-thumbnail-wrapper').html(item.find('.product-thumbnail-wrapper').html() + nav1);
        item.find('.product-thumbnail-wrapper').html(item.find('.product-thumbnail-wrapper').html() + nav2);
        item.find('.product-thumbnail-nav').attr('data-ind',0);
        item.find('.product-thumbnail-nav').attr('data-max',v.image.length);
      }      
    });            
  });  
  $('body').on('click','.product-thumbnail-nav div.prev', function() {
    let v = parseInt($(this).parent().attr('data-ind'));
    let v_pos = 0;
    if(v > 0)
      v = v -1;      
    else
      v = parseInt($(this).parent().attr('data-max')) -1;
    v_pos = v * 33.33334 + 1
    $('.product-thumbnail-wrapper').css('transform','translateX(-' + v_pos + '%)');
    $(this).parent().attr('data-ind',v);
    $('.product-popup-wrapper .product-thumbnail-item:nth-child(' + (v+1) + ')').trigger('click');
  });
  $('body').on('click','.product-thumbnail-nav div.next', function() {
    let v = parseInt($(this).parent().attr('data-ind'));
    let v_max = parseInt($(this).parent().attr('data-max'));
    let v_pos = 0;
    if(v == v_max-1)
      v = 0;
    else
      v = v + 1;
    v_pos = v * 33.33334 + 1
    $('.product-thumbnail-wrapper').css('transform','translateX(-' + v_pos + '%)');
    $(this).parent().attr('data-ind',v);
    $('.product-popup-wrapper .product-thumbnail-item:nth-child(' + (v+1) + ')').trigger('click');
  });  

});